import { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, ArrowUpCircle, ArrowDownCircle, Wallet, CreditCard, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { useAuth } from '../contexts/AuthContext';

const fmtMonto = (v) =>
  Number(v).toLocaleString('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 2 });

const fmtFecha = (v) =>
  new Date(v).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });

const fmtHora = (v) =>
  new Date(v).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });

// ── Formulario nuevo movimiento ──────────────────────────────────────────
const emptyForm = {
  caja_id: '', tipo: 'ingreso', monto: '', concepto: '',
  fecha: new Date().toISOString().slice(0, 10),
};

function MovimientoForm({ form, onChange, cajas }) {
  const setEv = (f) => (e) => onChange({ ...form, [f]: e.target.value });
  const set   = (f) => (v) => onChange({ ...form, [f]: v });

  return (
    <div className="space-y-4">

      {/* Tipo ingreso/egreso */}
      <div>
        <label>Tipo *</label>
        <div className="grid grid-cols-2 gap-2">
          {['ingreso', 'egreso'].map((t) => {
            const isActive = form.tipo === t;
            const isIngreso = t === 'ingreso';
            return (
              <button
                key={t}
                type="button"
                onClick={() => set('tipo')(t)}
                className={`flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all ${
                  isActive
                    ? isIngreso
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:border-emerald-500 dark:text-emerald-400'
                      : 'border-rose-500 bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:border-rose-500 dark:text-rose-400'
                    : 'border-slate-200 dark:border-slate-700 text-slate-400 hover:border-slate-300 dark:hover:border-slate-600'
                }`}
              >
                {isIngreso
                  ? <ArrowUpCircle size={16} />
                  : <ArrowDownCircle size={16} />}
                {isIngreso ? 'Ingreso' : 'Egreso'}
              </button>
            );
          })}
        </div>
      </div>

      {/* Caja */}
      <div>
        <label>Caja *</label>
        <select className="input" value={form.caja_id} onChange={setEv('caja_id')}>
          <option value="">— Seleccioná una caja —</option>
          {cajas.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
        </select>
      </div>

      {/* Monto + fecha */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label>Monto *</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
            <input
              className="input pl-7"
              type="number" min="0" step="0.01" placeholder="0,00"
              value={form.monto}
              onChange={setEv('monto')}
            />
          </div>
        </div>
        <div>
          <label>Fecha *</label>
          <input className="input" type="date" value={form.fecha} onChange={setEv('fecha')} />
        </div>
      </div>

      {/* Concepto */}
      <div>
        <label>Concepto *</label>
        <input
          className="input"
          placeholder="Ej: Venta mostrador, pago servicios..."
          value={form.concepto}
          onChange={setEv('concepto')}
          maxLength={255}
        />
      </div>
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────
export default function Cajas() {
  const { can } = useAuth();

  const [cajas,        setCajas]        = useState([]);
  const [selectedId,   setSelectedId]   = useState(null);
  const [movimientos,  setMovimientos]  = useState([]);
  const [loadingCajas, setLoadingCajas] = useState(true);
  const [loadingMov,   setLoadingMov]   = useState(false);

  const [filtros, setFiltros] = useState({ fecha_desde: '', fecha_hasta: '', tipo: '' });

  const [modalMov,  setModalMov]  = useState(false);
  const [modalCaja, setModalCaja] = useState(false);
  const [form,      setForm]      = useState(emptyForm);
  const [nombreCaja, setNombreCaja] = useState('');
  const [saving,    setSaving]    = useState(false);

  const [confirmMovId,  setConfirmMovId]  = useState(null);
  const [confirmCajaId, setConfirmCajaId] = useState(null);
  const [deleting,      setDeleting]      = useState(false);

  const puedeGestionar = can('tesoreria.movimientos');

  // ── Cargar cajas ──
  const fetchCajas = useCallback(async () => {
    setLoadingCajas(true);
    try {
      const res = await api.get('/cajas/index.php');
      setCajas(res.data);
      if (!selectedId && res.data.length > 0) setSelectedId(res.data[0].id);
    } catch { toast.error('Error al cargar cajas'); }
    finally { setLoadingCajas(false); }
  }, [selectedId]);

  useEffect(() => { fetchCajas(); }, []);  // eslint-disable-line

  // ── Cargar movimientos ──
  const fetchMovimientos = useCallback(async () => {
    if (!selectedId) return;
    setLoadingMov(true);
    try {
      const params = { caja_id: selectedId };
      if (filtros.fecha_desde) params.fecha_desde = filtros.fecha_desde;
      if (filtros.fecha_hasta) params.fecha_hasta = filtros.fecha_hasta;
      if (filtros.tipo)        params.tipo        = filtros.tipo;
      const res = await api.get('/movimientos-caja/index.php', { params });
      setMovimientos(res.data);
    } catch { toast.error('Error al cargar movimientos'); }
    finally { setLoadingMov(false); }
  }, [selectedId, filtros]);

  useEffect(() => { fetchMovimientos(); }, [fetchMovimientos]);

  // ── Nuevo movimiento ──
  const openMovModal = () => {
    setForm({ ...emptyForm, caja_id: selectedId ?? '' });
    setModalMov(true);
  };

  const handleSaveMov = async () => {
    if (!form.caja_id)                          { toast.error('Seleccioná una caja'); return; }
    if (!form.monto || parseFloat(form.monto) <= 0) { toast.error('El monto debe ser mayor a cero'); return; }
    if (!form.concepto.trim())                  { toast.error('El concepto es requerido'); return; }
    setSaving(true);
    try {
      await api.post('/movimientos-caja/index.php', {
        caja_id:  parseInt(form.caja_id),
        tipo:     form.tipo,
        monto:    parseFloat(form.monto),
        concepto: form.concepto.trim(),
        fecha:    form.fecha || null,
      });
      toast.success('Movimiento registrado');
      setModalMov(false);
      await Promise.all([fetchCajas(), fetchMovimientos()]);
    } catch (err) {
      toast.error(err?.message || 'Error al guardar');
    } finally { setSaving(false); }
  };

  // ── Nueva caja ──
  const handleSaveCaja = async () => {
    if (!nombreCaja.trim()) { toast.error('El nombre es requerido'); return; }
    setSaving(true);
    try {
      await api.post('/cajas/index.php', { nombre: nombreCaja.trim() });
      toast.success('Caja creada');
      setModalCaja(false);
      setNombreCaja('');
      fetchCajas();
    } catch (err) {
      toast.error(err?.message || 'Error al crear caja');
    } finally { setSaving(false); }
  };

  // ── Eliminar movimiento ──
  const handleDeleteMov = async () => {
    setDeleting(true);
    try {
      await api.delete(`/movimientos-caja/index.php?id=${confirmMovId}`);
      toast.success('Movimiento eliminado');
      setConfirmMovId(null);
      await Promise.all([fetchCajas(), fetchMovimientos()]);
    } catch (err) {
      toast.error(err?.message || 'Error al eliminar');
    } finally { setDeleting(false); }
  };

  // ── Eliminar caja ──
  const handleDeleteCaja = async () => {
    setDeleting(true);
    try {
      await api.delete(`/cajas/index.php?id=${confirmCajaId}`);
      toast.success('Caja eliminada');
      setConfirmCajaId(null);
      if (selectedId === confirmCajaId) setSelectedId(null);
      fetchCajas();
    } catch (err) {
      toast.error(err?.message || 'Error al eliminar');
    } finally { setDeleting(false); }
  };

  // ── Totales del período ──
  const totalIngresos = movimientos.filter(m => m.tipo === 'ingreso').reduce((s, m) => s + m.monto, 0);
  const totalEgresos  = movimientos.filter(m => m.tipo === 'egreso').reduce((s, m) => s + m.monto, 0);
  const saldoPeriodo  = totalIngresos - totalEgresos;

  const cajaActual = cajas.find(c => c.id === selectedId);
  const tieneFiltro = filtros.fecha_desde || filtros.fecha_hasta || filtros.tipo;

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Cajas</h1>
        {puedeGestionar && (
          <div className="flex gap-2">
            <button className="btn-secondary" onClick={() => setModalCaja(true)}>
              <Plus size={16} /> Nueva caja
            </button>
            <button className="btn-primary" onClick={openMovModal} disabled={!selectedId}>
              <Plus size={18} /> Nuevo movimiento
            </button>
          </div>
        )}
      </div>

      {/* Selector de cajas */}
      {loadingCajas ? (
        <div className="flex gap-3">
          {[1, 2].map(i => (
            <div key={i} className="h-24 w-48 rounded-2xl bg-slate-100 dark:bg-slate-800 animate-pulse" />
          ))}
        </div>
      ) : cajas.length === 0 ? (
        <div className="card flex flex-col items-center justify-center py-12 text-center">
          <Wallet size={36} strokeWidth={1.5} className="text-slate-200 dark:text-slate-700 mb-3" />
          <p className="text-sm text-slate-400">No hay cajas configuradas.</p>
          {puedeGestionar && (
            <button className="btn-primary mt-4" onClick={() => setModalCaja(true)}>
              <Plus size={16} /> Crear primera caja
            </button>
          )}
        </div>
      ) : (
        <div className="flex flex-wrap gap-3">
          {cajas.map(caja => {
            const isSelected = caja.id === selectedId;
            const saldoNeg   = caja.saldo < 0;
            return (
              <button
                key={caja.id}
                onClick={() => setSelectedId(caja.id)}
                className={`relative flex flex-col gap-2 px-5 py-4 rounded-2xl text-left transition-all duration-150 min-w-[160px] group
                  ${isSelected
                    ? 'bg-primary-500 shadow-card-md'
                    : 'bg-white dark:bg-slate-800 shadow-card hover:shadow-card-md hover:-translate-y-px'
                  }`}
              >
                <div className="flex items-center justify-between gap-6">
                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${
                    isSelected ? 'bg-white/20' : 'bg-primary-50 dark:bg-primary-500/10'
                  }`}>
                    <Wallet size={15} className={isSelected ? 'text-white' : 'text-primary-500 dark:text-primary-400'} />
                  </div>
                  {puedeGestionar && cajas.length > 1 && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setConfirmCajaId(caja.id); }}
                      className={`opacity-0 group-hover:opacity-100 p-1 rounded-lg transition-all ${
                        isSelected
                          ? 'hover:bg-white/20 text-white/60 hover:text-white'
                          : 'hover:bg-red-50 dark:hover:bg-red-500/10 text-slate-300 hover:text-red-500'
                      }`}
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
                <div>
                  <p className={`text-[11px] font-semibold uppercase tracking-wider mb-0.5 ${
                    isSelected ? 'text-white/70' : 'text-slate-400'
                  }`}>
                    {caja.nombre}
                  </p>
                  <p className={`text-lg font-bold tabular-nums leading-none ${
                    isSelected ? 'text-white' : saldoNeg ? 'text-rose-600 dark:text-rose-400' : 'text-slate-900 dark:text-white'
                  }`}>
                    {fmtMonto(caja.saldo)}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Filtros + resumen */}
      {selectedId && (
        <div className="card flex flex-wrap items-end gap-4">
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">Desde</label>
            <input
              className="input"
              type="date"
              value={filtros.fecha_desde}
              onChange={(e) => setFiltros(f => ({ ...f, fecha_desde: e.target.value }))}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">Hasta</label>
            <input
              className="input"
              type="date"
              value={filtros.fecha_hasta}
              onChange={(e) => setFiltros(f => ({ ...f, fecha_hasta: e.target.value }))}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">Tipo</label>
            <select
              className="input"
              value={filtros.tipo}
              onChange={(e) => setFiltros(f => ({ ...f, tipo: e.target.value }))}
            >
              <option value="">Todos</option>
              <option value="ingreso">Ingresos</option>
              <option value="egreso">Egresos</option>
            </select>
          </div>
          {tieneFiltro && (
            <button
              className="btn-secondary text-sm"
              onClick={() => setFiltros({ fecha_desde: '', fecha_hasta: '', tipo: '' })}
            >
              Limpiar
            </button>
          )}

          {movimientos.length > 0 && (
            <div className="ml-auto flex items-center gap-5">
              <div className="text-right">
                <p className="text-[10px] font-semibold text-emerald-500 uppercase tracking-wider">Ingresos</p>
                <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">{fmtMonto(totalIngresos)}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] font-semibold text-rose-500 uppercase tracking-wider">Egresos</p>
                <p className="text-sm font-bold text-rose-600 dark:text-rose-400 tabular-nums">{fmtMonto(totalEgresos)}</p>
              </div>
              <div className="h-8 w-px bg-slate-200 dark:bg-slate-700" />
              <div className="text-right">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Neto período</p>
                <p className={`text-sm font-bold tabular-nums ${saldoPeriodo >= 0 ? 'text-slate-900 dark:text-white' : 'text-rose-600 dark:text-rose-400'}`}>
                  {fmtMonto(saldoPeriodo)}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tabla de movimientos */}
      {selectedId && (
        <div className="card !p-0 overflow-hidden">
          {loadingMov ? (
            <div className="flex justify-center py-16">
              <div className="w-8 h-8 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin" />
            </div>
          ) : movimientos.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-300 dark:text-slate-700">
              <CreditCard size={48} strokeWidth={1.5} />
              <p className="text-sm mt-3 text-slate-400">
                {tieneFiltro ? 'Sin movimientos para los filtros aplicados' : `Sin movimientos en ${cajaActual?.nombre}`}
              </p>
              {puedeGestionar && !tieneFiltro && (
                <button className="btn-primary mt-4" onClick={openMovModal}>
                  <Plus size={16} /> Registrar primero
                </button>
              )}
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Tipo</th>
                    <th>Concepto</th>
                    <th className="text-right">Monto</th>
                    <th>Usuario</th>
                    {puedeGestionar && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {movimientos.map(m => (
                    <tr key={m.id}>
                      <td className="whitespace-nowrap text-slate-500 text-sm">
                        <span>{fmtFecha(m.fecha)}</span>
                        <span className="ml-1.5 text-slate-300 dark:text-slate-600 text-xs">{fmtHora(m.fecha)}</span>
                      </td>
                      <td>
                        {m.tipo === 'ingreso' ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 ring-1 ring-emerald-200/70 dark:ring-emerald-500/30">
                            <ArrowUpCircle size={11} /> Ingreso
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400 ring-1 ring-rose-200/70 dark:ring-rose-500/30">
                            <ArrowDownCircle size={11} /> Egreso
                          </span>
                        )}
                      </td>
                      <td className="text-slate-700 dark:text-slate-300 max-w-[280px] truncate">{m.concepto}</td>
                      <td className={`text-right font-semibold tabular-nums ${
                        m.tipo === 'ingreso' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'
                      }`}>
                        {m.tipo === 'egreso' && '−'}{fmtMonto(m.monto)}
                      </td>
                      <td className="text-slate-400 text-sm">{m.usuario}</td>
                      {puedeGestionar && (
                        <td>
                          <button
                            onClick={() => setConfirmMovId(m.id)}
                            className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-500/10 text-slate-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                          >
                            <Trash2 size={15} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modal: nuevo movimiento */}
      <Modal
        isOpen={modalMov}
        onClose={() => setModalMov(false)}
        title="Nuevo movimiento"
        footer={
          <>
            <button className="btn-secondary" onClick={() => setModalMov(false)} disabled={saving}>Cancelar</button>
            <button className="btn-primary" onClick={handleSaveMov} disabled={saving}>
              {saving ? 'Guardando...' : 'Registrar movimiento'}
            </button>
          </>
        }
      >
        <MovimientoForm form={form} onChange={setForm} cajas={cajas} />
      </Modal>

      {/* Modal: nueva caja */}
      <Modal
        isOpen={modalCaja}
        onClose={() => setModalCaja(false)}
        title="Nueva caja"
        footer={
          <>
            <button className="btn-secondary" onClick={() => setModalCaja(false)} disabled={saving}>Cancelar</button>
            <button className="btn-primary" onClick={handleSaveCaja} disabled={saving}>
              {saving ? 'Guardando...' : 'Crear caja'}
            </button>
          </>
        }
      >
        <div>
          <label>Nombre *</label>
          <input
            className="input"
            placeholder="Ej: Caja principal, Caja 2..."
            value={nombreCaja}
            onChange={(e) => setNombreCaja(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSaveCaja()}
          />
        </div>
      </Modal>

      {/* Confirm: eliminar movimiento */}
      <ConfirmDialog
        isOpen={!!confirmMovId}
        onClose={() => setConfirmMovId(null)}
        onConfirm={handleDeleteMov}
        loading={deleting}
        title="Eliminar movimiento"
        message="¿Seguro que querés eliminar este movimiento? El saldo de la caja se recalculará."
      />

      {/* Confirm: eliminar caja */}
      <ConfirmDialog
        isOpen={!!confirmCajaId}
        onClose={() => setConfirmCajaId(null)}
        onConfirm={handleDeleteCaja}
        loading={deleting}
        title="Eliminar caja"
        message="¿Seguro que querés eliminar esta caja? Se ocultará pero sus movimientos se conservan."
      />
    </div>
  );
}
